import { useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import type { CoachingNote } from "@/lib/types";
import { formatPlayhead } from "@/features/playback/useVideoPlayhead";
import { useThumbnail, invalidateThumbnail } from "@/features/coach-review/hooks/useThumbnail";
import { useMatches } from "@/features/matches/useMatches";
import {
  useCoachNoteMutations,
  useCoachNotesList,
} from "./hooks/useCoachNotesList";
import { PageHeader } from "./components/PageHeader";
import type { GoalComposerSource } from "./components/GoalComposerModal";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { useToast } from "@/components/ui/toast";
import { errorMessage } from "@/lib/api";

/**
 * Coach > Notes list page (M6a).
 *
 * Ports the legacy `renderCoachNotes` (`js/coaching/notes.js`):
 * the full team-notes list (video + observation rows), each with
 * a thumbnail tile, a context pill, a meta line, and the per-row
 * action set.
 *
 * Creation routes INTO Coach Review (the M4→M6 carry-in — the
 * note/observation composer lives there, built in M4):
 *   - **+ New note** → `/coach/review?source=video`
 *   - **+ New observation** → `/coach/review?source=tactical_board`
 *
 * Per-row actions:
 *   - **Open in Review** (video notes only) →
 *     `/coach/review?note_id=…` — Coach Review's M4 edit mode.
 *   - **Edit** → same `?note_id=` route (Coach Review picks the
 *     note vs. observation composer from `note_context`).
 *   - **Regenerate thumbnail** (video notes only) — best-effort
 *     server regen + a `useThumbnail` cache invalidation so the
 *     refreshed JPEG shows.
 *   - **Delete** — confirm + `deleteCoachingNote`.
 *
 * Observation rows show event metadata (type / title / date)
 * instead of match/timestamp/slot — the meta line never renders
 * a stale "0:00" / "Full" for fields that don't apply to an
 * observation. Observations also suppress "Open in Review" +
 * "Regenerate thumbnail" (no video frame to seek / thumbnail).
 */

const SLOT_LABELS: Record<string, string> = {
  full: "Full match",
  first_half: "1st half",
  second_half: "2nd half",
};

export type NotesPageProps = {
  /** M7.5 D1: opens the goal composer with the note as source
   * evidence. Undefined ⇒ the row's "+ New goal" button is
   * hidden (no dead button if the shell hasn't wired it). */
  onCreatePlayerGoal?: (
    playerId: string,
    source: GoalComposerSource,
  ) => void;
};

export function NotesPage({ onCreatePlayerGoal }: NotesPageProps = {}) {
  const navigate = useNavigate();
  const toast = useToast();
  const notesQuery = useCoachNotesList();
  const matchesQuery = useMatches();
  const mutations = useCoachNoteMutations();

  // Resolve `match_id` → a display label for video-note meta lines.
  const matchLabelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of matchesQuery.data ?? []) {
      const teams = `${m.home_team} vs ${m.away_team}`;
      map.set(m.id, m.date ? `${teams} · ${m.date}` : teams);
    }
    return map;
  }, [matchesQuery.data]);

  const notes = notesQuery.data?.notes ?? [];
  const [deleting, setDeleting] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<CoachingNote | null>(null);

  const handleDelete = (note: CoachingNote) => {
    setConfirmDelete(note);
  };

  const confirmDeleteNote = async () => {
    if (!confirmDelete) return;
    const note = confirmDelete;
    setConfirmDelete(null);
    setDeleting(note.id);
    try {
      await mutations.deleteNote(note.id);
      toast.success("Note deleted");
    } catch (err) {
      toast.error("Couldn't delete note", { description: errorMessage(err) });
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="flex flex-col gap-5" data-testid="notes-page">
      {confirmDelete ? (
        <ConfirmDialog
          title="Delete note?"
          message={`Delete "${confirmDelete.title?.trim() || confirmDelete.event_title?.trim() || "this note"}"? This can't be undone.`}
          confirmLabel="Delete"
          danger
          onConfirm={confirmDeleteNote}
          onClose={() => setConfirmDelete(null)}
          data-testid="notes-delete-confirm"
        />
      ) : null}
      <PageHeader
        title="Coaching Notes"
        description={
          <>
            Timestamped teaching points with optional drawings. Click{" "}
            <strong>Open in Review</strong> on any note to jump straight to
            the moment in the Review tab.
          </>
        }
        actions={
          <>
            <button
              type="button"
              onClick={() =>
                navigate({ to: "/coach/review", search: { source: "video" } })
              }
              data-testid="notes-new-note"
              className="bg-accent hover:bg-accent-hover text-white rounded px-4 py-1.5 text-sm font-display uppercase tracking-wide transition-colors"
            >
              + New note
            </button>
            <button
              type="button"
              onClick={() =>
                navigate({
                  to: "/coach/review",
                  search: { source: "tactical_board" },
                })
              }
              data-testid="notes-new-observation"
              className="border border-rule text-ink hover:bg-surface rounded px-4 py-1.5 text-sm font-display uppercase tracking-wide transition-colors"
            >
              + New observation
            </button>
          </>
        }
      />

      {notesQuery.isLoading ? (
        <p
          className="text-ink-muted text-sm font-mono px-3 py-6"
          data-testid="notes-loading"
        >
          Loading notes…
        </p>
      ) : notes.length === 0 ? (
        <p
          className="text-ink-muted text-sm px-3 py-6"
          data-testid="notes-empty"
        >
          No coaching notes yet. Use <strong>+ New note</strong> or{" "}
          <strong>+ New observation</strong> to add the first one.
        </p>
      ) : (
        <ul className="flex flex-col gap-2" data-testid="notes-list">
          {notes.map((note) => (
            <NoteRow
              key={note.id}
              note={note}
              matchLabel={
                note.match_id
                  ? (matchLabelById.get(note.match_id) ?? note.match_id)
                  : ""
              }
              deleting={deleting === note.id}
              onOpenInReview={() =>
                navigate({
                  to: "/coach/review",
                  search: { note_id: note.id },
                })
              }
              onRegenerate={async () => {
                try {
                  await mutations.regenerateThumbnail(note.id);
                  // Drop the cached blob so the refreshed JPEG shows.
                  invalidateThumbnail("note", note.id);
                  toast.success("Thumbnail regenerated");
                } catch (err) {
                  toast.error("Couldn't regenerate thumbnail", {
                    description: errorMessage(err),
                  });
                }
              }}
              onDelete={() => handleDelete(note)}
              // M7.5 D1: per-row "+ New goal" — gated like legacy
              // on `note.player_ids?.length` (a goal needs a target
              // player). The first tagged player becomes the
              // preselect; the modal lets the coach change it.
              onCreateGoal={
                onCreatePlayerGoal && note.player_ids?.length
                  ? () =>
                      onCreatePlayerGoal(note.player_ids![0], {
                        source_note_id: note.id,
                        seedTitle: note.title?.trim() || undefined,
                        seedDescription:
                          note.what_to_do_next?.trim() || undefined,
                      })
                  : undefined
              }
            />
          ))}
        </ul>
      )}
    </div>
  );
}

type NoteRowProps = {
  note: CoachingNote;
  matchLabel: string;
  deleting: boolean;
  onOpenInReview: () => void;
  onRegenerate: () => void;
  onDelete: () => void;
  /** M7.5 D1: undefined ⇒ no goal button (the gate is upstream:
   * `note.player_ids?.length > 0` AND the shell wired the
   * callback). */
  onCreateGoal?: () => void;
};

function NoteRow({
  note,
  matchLabel,
  deleting,
  onOpenInReview,
  onRegenerate,
  onDelete,
  onCreateGoal,
}: NoteRowProps) {
  const isObservation = note.note_context === "observation";
  const title =
    note.title?.trim() ||
    (isObservation
      ? note.event_title?.trim() || "Observation note"
      : "(untitled)");

  // Meta line — observation rows show event metadata, video rows
  // show match/timestamp/slot. Never a stale "0:00 / Full" for an
  // observation.
  const metaParts: string[] = [];
  if (isObservation) {
    if (note.event_type) {
      const t = note.event_type;
      metaParts.push(`${t[0].toUpperCase()}${t.slice(1)} observation`);
    } else {
      metaParts.push("Observation");
    }
    if (note.event_title && note.event_title !== title) {
      metaParts.push(note.event_title);
    }
    if (note.event_date) metaParts.push(note.event_date);
  } else {
    if (matchLabel) metaParts.push(matchLabel);
    metaParts.push(formatPlayhead(note.timestamp_seconds));
    if (note.slot) metaParts.push(SLOT_LABELS[note.slot] ?? note.slot);
  }
  if (note.category) metaParts.push(note.category);
  if (note.visibility) metaParts.push(note.visibility);

  return (
    <li
      className="flex flex-col items-stretch gap-3 rounded-lg border border-rule bg-bg-card px-3 py-2 sm:flex-row sm:items-center"
      data-testid="note-row"
      data-note-id={note.id}
      data-note-context={note.note_context}
    >
      <NoteThumb note={note} isObservation={isObservation} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span
            className="rounded px-1.5 py-0.5 text-[0.6rem] font-display uppercase tracking-wider bg-surface text-ink-muted"
            data-context-pill={note.note_context}
          >
            {isObservation ? "Observation" : "Video"}
          </span>
          <span className="font-display text-ink truncate">{title}</span>
        </div>
        <div className="text-ink-muted text-xs font-mono mt-0.5 truncate">
          {metaParts.join(" · ")}
        </div>
        {note.body?.trim() ? (
          <div className="text-ink-muted text-xs mt-0.5 truncate">
            {note.body.trim()}
          </div>
        ) : null}
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-1 sm:justify-end">
        {!isObservation ? (
          <RowButton
            label="Open in Review"
            testid="note-action-review"
            onClick={onOpenInReview}
          />
        ) : null}
        <RowButton
          label="Edit"
          testid="note-action-edit"
          // Edit routes to the SAME `?note_id=` Coach Review edit
          // mode — it picks note vs. observation composer from
          // `note_context`. (Observations have no "Open in Review",
          // but Edit applies to both.)
          onClick={onOpenInReview}
        />
        {!isObservation ? (
          <RowButton
            label="Regenerate thumbnail"
            testid="note-action-regenerate"
            onClick={onRegenerate}
          />
        ) : null}
        {/* M7.5 D1: per-row "+ New goal" — only when the note is
         * tagged to at least one player (gating handled upstream
         * by the callback presence). */}
        {onCreateGoal ? (
          <RowButton
            label="+ New goal"
            testid="note-action-goal"
            onClick={onCreateGoal}
          />
        ) : null}
        <RowButton
          label="Delete"
          testid="note-action-delete"
          onClick={onDelete}
          disabled={deleting}
          danger
        />
      </div>
    </li>
  );
}

/** Note thumbnail tile. Video notes fetch the access-controlled
 * JPEG via `useThumbnail`; observations have no video frame, so
 * they show a static clipboard placeholder with no network
 * request — mirrors the legacy `_coachNoteThumbHtml` split. */
function NoteThumb({
  note,
  isObservation,
}: {
  note: CoachingNote;
  isObservation: boolean;
}) {
  // Hook order must be stable — call `useThumbnail` always, but
  // pass `null` for observations so it never fetches.
  const url = useThumbnail("note", isObservation ? null : note.id);
  if (isObservation || !url) {
    return (
      <span
        className="block h-11 w-16 shrink-0 rounded bg-gradient-to-br from-canvas to-surface border border-rule/60"
        aria-hidden="true"
        data-testid="note-thumb-placeholder"
      />
    );
  }
  return (
    <img
      src={url}
      alt=""
      aria-hidden="true"
      data-testid="note-thumb"
      className="block h-11 w-16 shrink-0 rounded object-cover border border-rule/60"
    />
  );
}

function RowButton(props: {
  label: string;
  testid: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      disabled={props.disabled}
      data-testid={props.testid}
      title={props.label}
      aria-label={props.label}
      className={[
        "rounded border px-2 py-1 text-xs transition-colors disabled:opacity-40",
        props.danger
          ? "border-rule text-ink-muted hover:border-destructive hover:text-destructive"
          : "border-rule text-ink-muted hover:text-ink hover:bg-surface",
      ].join(" ")}
    >
      {props.label}
    </button>
  );
}
